import './App.css';
import { BrowserRouter, Route, Routes, Navigate, useParams } from 'react-router-dom';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import NavBar from './components/common/NavBar';
import ManageCategory from './components/admin/ManageCategory';
import CategoryList from './components/admin/CategoryList';
import ProductList from './components/admin/ProductList';
import ProductListing from './components/store/ProductListing';
import ManageProduct from './components/admin/ManageProduct';
import ProductView from './components/store/ProductView';
import Cart from './components/store/Cart';
import Checkout from './components/store/Checkout';
import OrderSuccess from './components/store/OrderSuccess';
import Orders from './components/admin/Orders';
import OrderDetails from './components/store/OrderDetails';
import UserOrders from './components/store/Orders';
import UserProfile from './components/auth/UserProfile';
import SiteSetting from './components/admin/SiteSetting';
import { useGlobal } from './contexts/GlobalContext'
import { Container, Spinner } from 'react-bootstrap';
import DeliveryLocationGenerator from './components/admin/DeliveryLocationGenerator';
import EarningsAnalytics from './components/admin/EarningsAnalytics';
import SiteAnalytics from './components/admin/SiteAnalytics';
import Footer from './components/common/Footer';
import { Utils } from './common/Utils';
import Setup from './components/Setup';
import SetupInstructions from './components/admin/SetupInstructions';
import RegisterStoreOperator from './components/admin/StoreOperator';
import PaymentProviders from './components/admin/PaymentProviders';
import { ReactNode } from 'react';
import FeedbackForm from './components/Feedback';
import PasswordReset from './components/auth/PasswordReset';


function App() {
  const { user, userloaded, settingsLoaded, isAdmin, settings, site } = useGlobal();
  document.title = settings ? settings.siteTitle : "OrderQ";

  const LoginRedirect = () => {
    const { returnpath } = useParams();
    return <Navigate to={Utils.GetUrl(`/${returnpath}`)} replace />;
  };

  if (!settingsLoaded || !userloaded) {
    return <Container className="d-flex flex-column justify-content-center align-items-center vh-100">
      {settings && settings.siteLoadingIcon ? <img src={settings.siteLoadingIcon} alt="Centered" className="img-fluid" /> : <Spinner animation="border" variant="primary" role="status"></Spinner>}
    </Container>
  }
  else if (!settings || settings.isDefault) {

    if (site && !site.siteLoaded) {
      return <Container className="d-flex flex-column justify-content-center align-items-center vh-100">
        {<><img src="/icon2.png" alt="Centered" className="img-fluid" /><br /><h2 style={{ color: "#304768" }}>We are busy setting up your site</h2><h4 style={{ color: "#304768" }}>This usually takes about 5-minutes to setup. Once complete you will be notified.</h4></>}
      </Container>
    }

    return <Container className="d-flex flex-column justify-content-center align-items-center vh-100">
      {<><img src="/icon2.png" alt="Centered" className="img-fluid" /><br /><h2 style={{ color: "#304768" }}>Store Not Found</h2></>}
    </Container>
  }

  interface RedirectIfAuthenticatedProps {
    children: ReactNode;
  }

  const RedirectIfNotAuth: React.FC<RedirectIfAuthenticatedProps> = ({ children }) => {
    return user ? <>{children}</> : <Navigate to={Utils.GetUrl("/login")} />;
  };

  const RedirectIfAdminNotAuth: React.FC<RedirectIfAuthenticatedProps> = ({ children }) => {
    return user && isAdmin ? <>{children}</> : user ? <Navigate to="/" /> : <Navigate to={Utils.GetUrl("/admin")} />;
  };

  return (
    <>
      {settings && settings.siteFavicon && <link rel='icon' href={settings.siteFavicon}></link>}
      {site && site.package !== "Starter" && settings && settings.siteCSS && settings.siteCSS !== "" && <style dangerouslySetInnerHTML={{ __html: settings.siteCSS.replace(/\n/g, '').trim() }} />}
      <BrowserRouter>
        <NavBar />
        <Routes>
          <Route path="/" element={<ProductListing />} />
          <Route
            path="/login/:returnpath"
            element={user ? <LoginRedirect /> : <Login />}
          />
          <Route
            path="/login"
            element={user ? <Navigate to="/" /> : <Login />} // Redirect if logged in
          />
          <Route
            path="/reset-password"
            element={<PasswordReset />}
          />          
          <Route
            path="/admin"
            element={user ? <Navigate to="/admin/instructions" /> : <Login />} // Redirect if logged in
          />
          <Route
            path="/register"
            element={user ? <Navigate to="/" /> : <Register />} // Redirect if logged in
          />

          <Route path="/admin/categories" element={<RedirectIfAdminNotAuth><CategoryList /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/category/:categoryId" element={<RedirectIfAdminNotAuth><ManageCategory /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/category" element={<RedirectIfAdminNotAuth><ManageCategory /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/products" element={<RedirectIfAdminNotAuth><ProductList /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/product/:productId" element={<RedirectIfAdminNotAuth><ManageProduct /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/product" element={<RedirectIfAdminNotAuth><ManageProduct /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/settings" element={<RedirectIfAdminNotAuth><SiteSetting /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/labelgenerator" element={<RedirectIfAdminNotAuth><DeliveryLocationGenerator /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/earningsanalytics" element={<RedirectIfAdminNotAuth><EarningsAnalytics /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/siteanalytics" element={<RedirectIfAdminNotAuth><SiteAnalytics /></RedirectIfAdminNotAuth>} />
          <Route path="/:role/order/:orderId" element={<RedirectIfAdminNotAuth><OrderDetails /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/orders" element={<RedirectIfAdminNotAuth><Orders /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/instructions" element={<RedirectIfAdminNotAuth><SetupInstructions /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/storeoperator" element={<RedirectIfAdminNotAuth><RegisterStoreOperator /></RedirectIfAdminNotAuth>} />
          <Route path="/admin/paymentproviders" element={<RedirectIfAdminNotAuth><PaymentProviders /></RedirectIfAdminNotAuth>} />

          <Route path="/orders" element={<RedirectIfNotAuth><UserOrders /></RedirectIfNotAuth>} />
          <Route path="/order/:orderId" element={<RedirectIfNotAuth><OrderDetails /></RedirectIfNotAuth>} />
          <Route path="/userprofile" element={<RedirectIfNotAuth><UserProfile /></RedirectIfNotAuth>} />
          <Route path="/feedback" element={<RedirectIfNotAuth><FeedbackForm /></RedirectIfNotAuth>} />

          <Route path="/d/:deliveryLocation" element={<ProductListing />} />
          <Route path="/products" element={<ProductListing />} />
          <Route path="/products/:categoryId" element={<ProductListing />} />
          <Route path="/product/:productId" element={<ProductView />} />
          <Route path="/product/:productId/:hasattributes/:qty" element={<ProductView />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/cart/:orderId" element={<Cart />} />
          <Route path="/checkout/orderfail/:orderId" element={<Checkout />} />
          <Route path="/checkout/ordercancel/:orderId" element={<Checkout />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/ordersuccess/:orderNumber" element={<OrderSuccess />} />
          <Route path="/setup" element={<Setup />} />

          <Route path="/:alias/" element={<ProductListing />} />
          <Route
            path="/:alias/login/:returnpath"
            element={user ? <LoginRedirect /> : <Login />}
          />
          <Route
            path="/:alias/login"
            element={user ? <Navigate to={`/${Utils.GetAlias()}`} /> : <Login />} // Redirect if logged in
          />
          <Route
            path="/:alias/admin"
            element={user ? <Navigate to={`/${Utils.GetAlias()}/admin/instructions`} /> : <Login />} // Redirect if logged in
          />
          <Route
            path="/:alias/register"
            element={user ? <Navigate to={`/${Utils.GetAlias()}`} /> : <Register />} // Redirect if logged in
          />
          <Route path="/:alias/admin/categories" element={<RedirectIfAdminNotAuth><CategoryList /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/category/:categoryId" element={<RedirectIfAdminNotAuth><ManageCategory /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/category" element={<RedirectIfAdminNotAuth><ManageCategory /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/products" element={<RedirectIfAdminNotAuth><ProductList /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/product/:productId" element={<RedirectIfAdminNotAuth><ManageProduct /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/product" element={<RedirectIfAdminNotAuth><ManageProduct /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/settings" element={<RedirectIfAdminNotAuth><SiteSetting /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/:role/order/:orderId" element={<RedirectIfAdminNotAuth><OrderDetails /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/orders" element={<RedirectIfAdminNotAuth><Orders /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/labelgenerator" element={<RedirectIfAdminNotAuth><DeliveryLocationGenerator /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/earningsanalytics" element={<RedirectIfAdminNotAuth><EarningsAnalytics /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/siteanalytics" element={<RedirectIfAdminNotAuth><SiteAnalytics /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/instructions" element={<RedirectIfAdminNotAuth><SetupInstructions /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/storeoperator" element={<RedirectIfAdminNotAuth><RegisterStoreOperator /></RedirectIfAdminNotAuth>} />
          <Route path="/:alias/admin/paymentproviders" element={<RedirectIfAdminNotAuth><PaymentProviders /></RedirectIfAdminNotAuth>} />

          <Route path="/:alias/orders" element={<RedirectIfNotAuth><UserOrders /></RedirectIfNotAuth>} />
          <Route path="/:alias/order/:orderId" element={<RedirectIfNotAuth><OrderDetails /></RedirectIfNotAuth>} />
          <Route path="/:alias/userprofile" element={<RedirectIfNotAuth><UserProfile /></RedirectIfNotAuth>} />
          <Route path="/:alias/feedback" element={<RedirectIfNotAuth><FeedbackForm /></RedirectIfNotAuth>} />

          <Route path="/:alias/d/:deliveryLocation" element={<ProductListing />} />
          <Route path="/:alias/products" element={<ProductListing />} />
          <Route path="/:alias/products/:categoryId" element={<ProductListing />} />
          <Route path="/:alias/product/:productId" element={<ProductView />} />
          <Route path="/:alias/product/:productId/:hasattributes/:qty" element={<ProductView />} />
          <Route path=":alias/cart/" element={<Cart />} />
          <Route path="/:alias/cart/:orderId" element={<Cart />} />
          <Route path="/:alias/checkout" element={<Checkout />} />
          <Route path="/:alias/checkout/orderfail/:orderId" element={<Checkout />} />
          <Route path="/:alias/checkout/ordercancel/:orderId" element={<Checkout />} />
          <Route path="/:alias/ordersuccess/:orderNumber" element={<OrderSuccess />} />
          <Route path="/:alias/setup" element={<Setup />} />
        </Routes>
        <Footer />
      </BrowserRouter>
    </>
  );
}

export default App;
